<script setup lang="ts">
import { computed, inject, type Ref, ref, toValue } from "vue";
import JSONtoHTML from "@/components/forms/SharedComponents/WYSIWYG/JSONtoHTML.vue";
import { useExcursionOccurrenceDates } from "@/components/RegistrationProcess/composables/useExcursionOccurrenceDates.ts";
import { ExcursionsKey } from "@/components/RegistrationProcess/composables/useExcursions.ts";
import type { ConfiguratorOptions } from "@/components/RegistrationProcess/types/configurator.ts";
import type { Excursion } from "@/components/RegistrationProcess/types/events.ts";
import { getEventDisplayLocation } from "@/components/RegistrationProcess/utils/eventDisplayLocation";
import { getEventDuration } from "@/components/RegistrationProcess/utils/eventDuration";
import CalendarIcon from "@/components/svg-icons/CalendarIcon.vue";
import SearchChevron from "@/components/svg-icons/SearchChevron.vue";
import { useMobile } from "@/composables/useMobile";
import { useTenantTheming } from "@/composables/useTenantTheming.ts";
import MinusSignCircle from "../../widgets/MinusSignCircle.vue";
import ExcursionCardDetail from "../ExcursionCardDetail/index.vue";
import ExcursionImages from "../ExcursionImages/index.vue";
import type { Props } from "./types.ts";

defineOptions({
  name: "ExcursionOptionalItem",
});

const modelValue = defineModel<Excursion>({ required: true });
const props = withDefaults(defineProps<Props>(), {
  title: "ExcursionOptionalItem",
});

// The storefront color stays on the CTA, chevron and select button. Accent surfaces (subcategory
// band, price pill) go gray with navy text for branded tenants; unbranded API tenants keep teal.
const { secondaryColor, secondaryColorClass } = useTenantTheming();

const priceTextClass = computed<string>(() =>
  secondaryColor.value ? "text-university-primary" : "text-university-secondary",
);

const { addRegistration, removeRegistration, excursions, sessionExcursions, isExcursionIncluded } =
  inject(ExcursionsKey)!;
const { configuratorOptions } = inject<{ configuratorOptions: Ref<ConfiguratorOptions> }>("configuratorOptions")!;

const optionalSessionExcursions = computed<Excursion[]>(
  () => sessionExcursions.value?.filter((e) => !isExcursionIncluded(e)) ?? [],
);

const areDetailsVisible = ref(false);

const { isMobile } = useMobile();

const svgSecondaryColor = computed<string>(() =>
  secondaryColor.value && secondaryColor.value.length > 0 ? secondaryColor.value : "#007F80",
);

const optionsPricing = computed(() => {
  const excursions = toValue(configuratorOptions).event_occurrences;
  return excursions.find((e) => e.productId === modelValue.value?.id);
});

const price = computed<string>(() => {
  return optionsPricing.value?.excursionPriceLabel(false) ?? "";
});

const event = computed(() => modelValue.value?.event ?? {});

const location = computed(() => getEventDisplayLocation(event.value, { multiplePhysicalLocations: "join" }));

const title = computed(() => {
  return event.value?.title;
});

const duration = computed<string>(() => getEventDuration(event.value));

const description = computed<Record<string, any>>(() => {
  return event.value?.description as Record<string, any>;
});

const subcategory = computed(() => event.value?.event_subcategory?.name ?? "");

const viewButtonVerbiage = computed(() => (isMobile.value ? "View " : "View Details"));

const isExcursionSelected = computed(() => {
  return excursions.eventInstanceIds.includes(modelValue.value.id);
});

const selectionText = computed(() => {
  return isExcursionSelected.value ? "Selected" : isMobile.value ? "Add" : "Add Excursion";
});

const { formattedDateRange, showOccurrenceDates, showSwitchDatesMessage } = useExcursionOccurrenceDates(
  modelValue,
  optionalSessionExcursions,
);

const handleViewDetailsClick = () => {
  areDetailsVisible.value = true;
};

const selectExcursion = () => {
  const excursionId = modelValue.value.id;
  if (excursions.eventInstanceIds.includes(excursionId)) {
    removeRegistration(excursionId);
  } else {
    addRegistration(excursionId);
  }
};
</script>

<template>
  <ExcursionCardDetail
    v-model="modelValue"
    v-model:visible="areDetailsVisible"
  />
  <div data-testid="excursion-optional-item">
    <div class="grid grid-cols-1 md:grid-cols-2 shadow-[0px_4px_20px_2px_#0000001A]">
      <div class="flex flex-col min-h-0" :class="{ relative: isMobile }">
        <div
          v-if="!isMobile || subcategory"
          class="font-bold text-sm text-university-primary"
          :class="[
            secondaryColor ? 'bg-gray-275' : 'bg-teal-accent',
            isMobile
              ? 'absolute top-0 left-0 z-10 px-[18px] py-3 tracking-[0.3px]'
              : 'h-50p shrink-0 p-4 flex items-center justify-start w-full',
          ]"
        >
          <span data-testid="excursion-optional-item-subcategory">{{ subcategory }}</span>
        </div>
        <!-- Multi-image: fills remaining vertical space -->
        <div class="flex w-full items-center justify-start">
          <ExcursionImages :model-value="event.image ?? ''" :image-alt="title" :resolution="'medium'" />
        </div>
      </div>
      <div class="h-full">
        <div class="flex flex-col h-full p-6 gap-4 text-university-primary">
          <div v-if="price" class="flex flex-row items-center" :class="isMobile ? 'justify-start' : 'justify-end'">
            <div
              class="leading-none"
              :class="[isMobile ? 'px-3 py-[3px]' : 'p-1', secondaryColor ? 'bg-gray-275' : 'bg-teal-400/20']"
            >
              <span class="uppercase font-bold text-sm leading-none" :class="priceTextClass" data-testid="excursion-optional-item-price">
                {{ price }}
              </span>
            </div>
          </div>

          <div class="text-2xl font-bold leading-none" data-testid="excursion-optional-item-title">
            {{ title }}
          </div>

          <div class="flex flex-col gap-1 text-sm">
            <div v-if="location" data-testid="excursion-optional-item-location">{{ location }}</div>
            <div v-if="duration" data-testid="excursion-optional-item-duration">{{ duration }}</div>
          </div>

          <div class="text-sm" data-testid="excursion-optional-item-description">
            <JSONtoHTML class="text-base text-university-primary" :tiptap-json="description" custom-class="line-clamp-3" />
          </div>

          <div v-if="showOccurrenceDates" class="flex items-center gap-2.5 flex-wrap">
            <div class="flex items-center gap-2 text-sm font-semibold">
              <CalendarIcon class="w-4 h-4" :style="{ color: svgSecondaryColor }" />
              <span>{{ formattedDateRange }}</span>
            </div>
            <span v-if="showSwitchDatesMessage" class="basis-full text-xs font-medium text-university-primary/60">
              Add now, switch dates later
            </span>
          </div>

          <div class="grow"></div>

          <div
            class="flex flex-col-reverse gap-3 md:flex-row md:justify-between md:items-baseline md:gap-0"
            :class="`${secondaryColorClass}`"
          >
            <div
              class="items-center gap-[0.625rem] uppercase font-bold flex cursor-pointer justify-center md:justify-start"
              data-testid="excursion-optional-item-view-details"
              @click="handleViewDetailsClick()"
            >
              <span>{{ viewButtonVerbiage }}</span>
              <div>
                <SearchChevron rotateDirection="right" :fillColor="svgSecondaryColor" :width="14" :height="9" />
              </div>
            </div>

            <button
              class="flex relative items-center justify-center gap-2 p-4 font-bold uppercase border-2 w-full md:w-auto"
              data-testid="excursion-optional-item-toggle-button"
              @click="selectExcursion()"
            >
              <span>
                {{ selectionText }}
              </span>
              <div v-if="isExcursionSelected" class="absolute -top-3 -right-3">
                <MinusSignCircle />
              </div>
              <div v-else>
                <span>+</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
